import { createAsyncThunk } from '@reduxjs/toolkit'
import { serializableError } from '@utils/serializable_error'
import { PermissionRole, SerializableError } from '@app-types/general'
import jsonApiNormalize from '@base/json-api-normalizer'
import PermissionWebAPI from '@api/PermissionWebAPI'
import { decodeAllStrings } from '@utils/decoder'

type FulfilledThunkResponse = {
  json: any // Type based on API response JSON structure
  normalized: any
}

type FetchPermissionablePermissionsParams = {
  resourceId: string | number
  resourceType: string
}

export const fetchPermissionablePermissions = createAsyncThunk<
  FulfilledThunkResponse, // Return type when fulfilled
  FetchPermissionablePermissionsParams, // Argument type
  { rejectValue: SerializableError } // Type of rejection payload
>(
  'resourcePermissions/fetchPermissionablePermissions',
  async (params: FetchPermissionablePermissionsParams, thunkAPI) => {
    const { resourceId, resourceType } = params
    const { rejectWithValue } = thunkAPI
    try {
      const response = await PermissionWebAPI.loadPermissionsForPermissionable(resourceType, resourceId)
      const normalized = jsonApiNormalize(response.data, { camelizeKeys: false, camelizeTypeValues: false })
      return {
        json: response.data,
        normalized,
      }
    } catch (error) {
      return rejectWithValue(serializableError(error))
    }
  }
)

type CreatePermissionParams = {
  permissionable_id: string | number
  permissionable_type: string
  permissable_type: string

  // should have at least permissable_identifiers, or permissable_id
  permissable_identifiers?: string[]
  permissable_id?: string | number

  role: PermissionRole
}

export const createPermission = createAsyncThunk<
  FulfilledThunkResponse, // Return type when fulfilled
  CreatePermissionParams, // Argument type
  { rejectValue: SerializableError } // Type of rejection payload
>('resourcePermissions/createPermission', async (params, thunkAPI) => {
  const { rejectWithValue } = thunkAPI
  try {
    const data = {
      data: {
        type: 'permissions',
        attributes: params,
      },
    }
    const response = await PermissionWebAPI.createPermission(data)
    const normalized = jsonApiNormalize(response.data, { camelizeKeys: false, camelizeTypeValues: false })

    // return payload
    return {
      json: response.data,
      normalized,
    }
  } catch (error) {
    return rejectWithValue(serializableError(error))
  }
})

type BulkCreatePermissionsParams = {
  // Note: We should attempt to conform to the JSON::API spec. Probably by sending an array of permissions. E.g.
  // permissions: CreatePermissionParams[]
  // but this will need some work on the backend to handle bulk creation

  permissionable_id: string | number
  permissionable_type: string
  permissable_identifiers: string[]
  permissable_type: string
  role: PermissionRole
}

export const bulkCreatePermissions = createAsyncThunk<
  FulfilledThunkResponse, // Return type when fulfilled
  BulkCreatePermissionsParams, // Argument type
  { rejectValue: SerializableError } // Type of rejection payload
>('resourcePermissions/bulkCreatePermission', async (params, thunkAPI) => {
  const { rejectWithValue } = thunkAPI
  try {
    const data = {
      data: {
        type: 'permissions',
        attributes: params,
      },
    }
    const response = await PermissionWebAPI.bulkCreatePermissions(data)
    const normalized = jsonApiNormalize(response.data, { camelizeKeys: false, camelizeTypeValues: false })

    // return payload for successfully created permissions
    // Note: May have been a partial success, so we also check for meta errors in the reducer
    return {
      json: response.data,
      normalized,
    }
  } catch (error) {
    // finally reject the promise with the error
    return rejectWithValue(serializableError(error))
  }
})

export const revokePermission = createAsyncThunk<
  FulfilledThunkResponse, // Return type when fulfilled
  string | number, // Argument type (permissionId)
  { rejectValue: SerializableError } // Type of rejection payload
>('resourcePermissions/revokePermission', async (permissionId, thunkAPI) => {
  const { rejectWithValue } = thunkAPI
  try {
    const response = await PermissionWebAPI.revokePermission(permissionId)
    const normalized = jsonApiNormalize(response.data, { camelizeKeys: false, camelizeTypeValues: false })

    // return payload
    return {
      json: response.data,
      normalized,
    }
  } catch (error) {
    return rejectWithValue(serializableError(error))
  }
})

export const fetchGroups = createAsyncThunk<
  FulfilledThunkResponse, // Return type when fulfilled
  void, // Argument type (none)
  { rejectValue: SerializableError } // Type of rejection payload
>('resourcePermissions/fetchGroups', async (params, thunkAPI) => {
  const { rejectWithValue } = thunkAPI
  try {
    const response = await PermissionWebAPI.fetchGroups()

    // Ensure HTML entities in group names like "Sales & Marketing" are proeprly decoded
    const decodedData = decodeAllStrings(response.data)
    const normalized = jsonApiNormalize(decodedData, { camelizeKeys: false, camelizeTypeValues: false })

    // return payload
    return {
      json: decodedData,
      normalized,
    }
  } catch (error) {
    return rejectWithValue(serializableError(error))
  }
})
